import { TIME_AXIS_HEIGHT_PX } from "../constants";
import { POSITION_CLOSE_TOUCH_SIZE_PX } from "../overlays/positions/positionConstants";

/** Parts of the chart that react to the pointer differently. */
export const REGION = {
  PANE: "pane",
  PRICE_AXIS: "priceAxis",
  TIME_AXIS: "timeAxis",
  // Close button on the card of an open position
  POSITION_CLOSE: "positionClose",
  NONE: "none",
};

// TradingView cursors: a resize arrow over the time axis; the price axis is not dragged (always autoscaled)
const REGION_CURSORS = {
  [REGION.PANE]: "default",
  [REGION.PRICE_AXIS]: "default",
  [REGION.TIME_AXIS]: "ew-resize",
  [REGION.POSITION_CLOSE]: "pointer",
  [REGION.NONE]: "default",
};
export const DRAG_CURSOR = "grabbing";

/**
 * Oscillator panes under the price pane are PANE too (pan and zoom); the
 * price axis next to them does nothing yet (their scales autoscale).
 *
 * @param {{ x: number, y: number }} point - CSS px of the chart
 * @param {{ pane: { width: number, height: number }, plotHeight: number, priceAxisWidth: number }} layout
 * @returns {string} REGION value; NONE outside the chart and in the corner under the price axis
 */
export const regionAt = ({ x, y }, { pane, plotHeight, priceAxisWidth }) => {
  const isInChart =
    x >= 0 && y >= 0 && x < pane.width + priceAxisWidth && y < plotHeight + TIME_AXIS_HEIGHT_PX;

  if (!isInChart) return REGION.NONE;

  if (y < pane.height) return x < pane.width ? REGION.PANE : REGION.PRICE_AXIS;
  if (y < plotHeight) return x < pane.width ? REGION.PANE : REGION.NONE;

  return x < pane.width ? REGION.TIME_AXIS : REGION.NONE;
};

/**
 * The area of the oscillator panes, with their price axis (it scrolls when they do not fit, D24).
 *
 * @param {{ x: number, y: number }} point - CSS px of the chart
 * @param {{ pane: object, paneArea: { top: number, height: number }, priceAxisWidth: number }} layout
 * @returns {boolean}
 */
export const isInPaneArea = ({ x, y }, { pane, paneArea, priceAxisWidth }) =>
  x >= 0 && x < pane.width + priceAxisWidth && y >= paneArea.top && y < paneArea.top + paneArea.height;

/** @param {string} region - REGION value */
export const cursorFor = (region) => REGION_CURSORS[region];

const centreOf = ({ x, y, width, height }) => ({ x: x + width / 2, y: y + height / 2 });

// A finger gets at least POSITION_CLOSE_TOUCH_SIZE_PX square around the button (E16)
const hitRect = (rect, isTouch) => {
  if (!isTouch) return rect;

  const width = Math.max(rect.width, POSITION_CLOSE_TOUCH_SIZE_PX);
  const height = Math.max(rect.height, POSITION_CLOSE_TOUCH_SIZE_PX);
  const centre = centreOf(rect);

  return { x: centre.x - width / 2, y: centre.y - height / 2, width, height };
};

const contains = ({ x, y, width, height }, point) =>
  point.x >= x && point.x < x + width && point.y >= y && point.y < y + height;

/**
 * Close button of a position card under the pointer. Touch areas of
 * stacked cards may overlap, then the nearest button wins.
 *
 * @param {{ x: number, y: number }} point - CSS px of the chart
 * @param {Array<{ positionId: string|number, rect: object }>} buttons - positionsState.closeButtons
 * @param {boolean} isTouch
 * @returns {string|number|null} id of the position, null when no button is there
 */
export const positionCloseAt = (point, buttons, isTouch) => {
  let nearest = null;
  let nearestDistance = Infinity;

  buttons.forEach(({ positionId, rect }) => {
    if (!contains(hitRect(rect, isTouch), point)) return;

    const centre = centreOf(rect);
    const distance = Math.hypot(point.x - centre.x, point.y - centre.y);

    if (distance < nearestDistance) {
      nearest = positionId;
      nearestDistance = distance;
    }
  });

  return nearest;
};
